import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeftIcon,
  UserIcon,
  PhoneIcon,
  BuildingOfficeIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import ImageUpload from '../components/ui/ImageUpload';
import MedicalInformationSection from '../components/executive-protection/MedicalInformationSection';
import { executiveProtectionService } from '../services/executive-protection.service';

export interface ExecutiveFormData {
  firstName: string;
  lastName: string;
  title: string;
  department: string;
  phone: string;
  email: string;
  riskLevel: 'low' | 'medium' | 'high' | 'critical';

  // Home Address
  homeAddressLine1?: string;
  homeAddressLine2?: string;
  homeCity?: string;
  homeState?: string;
  homeZipCode?: string;
  homeCountry?: string;

  // Emergency Contact
  emergencyContactName?: string;
  emergencyContactRelationship?: string;
  emergencyContactPhone?: string;
  emergencyContactEmail?: string;

  // Medical Information
  insuranceProvider?: string;
  insurancePolicyNumber?: string;
  insuranceGroupNumber?: string;

  primaryDoctorName?: string;
  primaryDoctorPhone?: string;
  primaryDoctorAddress?: string;

  medicalConditions?: string[];

  medicalDisclosureConsent?: boolean;
  consentDate?: string;
  consentExpiresAt?: string;

  notes?: string;
  profilePicture?: File;
  insuranceCardFront?: File;
  insuranceCardBack?: File;
  consentForm?: File;
}

export default function CreateProtectedPerson() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<ExecutiveFormData>({
    firstName: '',
    lastName: '',
    title: '',
    department: '',
    phone: '',
    email: '',
    riskLevel: 'medium',
    homeAddressLine1: '',
    homeAddressLine2: '',
    homeCity: '',
    homeState: '',
    homeZipCode: '',
    homeCountry: 'USA',
    emergencyContactName: '',
    emergencyContactRelationship: '',
    emergencyContactPhone: '',
    emergencyContactEmail: '',
    notes: '',
  });

  const [errors, setErrors] = useState<Partial<Record<keyof ExecutiveFormData, string>>>({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  // Mutation for creating protected person
  const createPersonMutation = useMutation({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    mutationFn: (data: Omit<ExecutiveFormData, 'profilePicture' | 'insuranceCardFront' | 'insuranceCardBack' | 'consentForm'>) => executiveProtectionService.createProtectedPerson(data as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['executive-protection', 'persons'] });
      queryClient.invalidateQueries({ queryKey: ['executive-protection', 'stats'] });
    },
  });

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof ExecutiveFormData, string>> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    if (!formData.department.trim()) {
      newErrors.department = 'Department is required';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\+?[\d\s\-()]+$/.test(formData.phone)) {
      newErrors.phone = 'Invalid phone number format';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setApiError(null); // Clear any previous API errors
    try {
      // Helper function to convert date strings to ISO 8601 datetime format
      const formatDateTime = (dateStr?: string): string | undefined => {
        if (!dateStr || dateStr.trim() === '') { return undefined; }
        // Append time and timezone to convert YYYY-MM-DD to ISO 8601
        return new Date(dateStr + 'T00:00:00Z').toISOString();
      };

      // First create the person with all fields including medical information
      // Note: Don't include 'location' or 'status' fields - they don't exist in backend schema
      // Backend uses 'isActive' instead of 'status', and it defaults to true on creation
      const newPerson = await createPersonMutation.mutateAsync({
        firstName: formData.firstName,
        lastName: formData.lastName,
        title: formData.title,
        department: formData.department,
        riskLevel: formData.riskLevel,
        phone: formData.phone,
        email: formData.email,

        // Home Address
        homeAddressLine1: formData.homeAddressLine1,
        homeAddressLine2: formData.homeAddressLine2,
        homeCity: formData.homeCity,
        homeState: formData.homeState,
        homeZipCode: formData.homeZipCode,
        homeCountry: formData.homeCountry ?? 'USA',

        // Emergency Contact
        emergencyContactName: formData.emergencyContactName,
        emergencyContactRelationship: formData.emergencyContactRelationship,
        emergencyContactPhone: formData.emergencyContactPhone,
        emergencyContactEmail: formData.emergencyContactEmail,

        // Medical Information
        insuranceProvider: formData.insuranceProvider,
        insurancePolicyNumber: formData.insurancePolicyNumber,
        insuranceGroupNumber: formData.insuranceGroupNumber,

        primaryDoctorName: formData.primaryDoctorName,
        primaryDoctorPhone: formData.primaryDoctorPhone,
        primaryDoctorAddress: formData.primaryDoctorAddress,

        medicalConditions: formData.medicalConditions,

        // Medical Disclosure Consent - properly formatted dates
        medicalDisclosureConsent: formData.medicalDisclosureConsent,
        consentDate: formatDateTime(formData.consentDate),
        consentExpiresAt: formatDateTime(formData.consentExpiresAt),

        notes: formData.notes,
      });

      // If there's a profile picture, upload it
      if (formData.profilePicture && newPerson?.id) {
        try {
          await executiveProtectionService.uploadProfilePicture(newPerson.id, formData.profilePicture);
          // Invalidate queries to refresh the data with the new profile picture
          queryClient.invalidateQueries({ queryKey: ['executive-protection', 'persons'] });
        } catch (_uploadError) {
          // Don't fail the entire operation if just the image upload fails
        }
      }

      // Upload medical attachments if provided
      if (newPerson?.id) {
        // Upload insurance card front
        if (formData.insuranceCardFront) {
          try {
            await executiveProtectionService.uploadMedicalAttachment(
              newPerson.id,
              formData.insuranceCardFront,
              'INSURANCE_CARD_FRONT'
            );
          } catch (_uploadError) {
            // Ignore upload error
          }
        }

        // Upload insurance card back
        if (formData.insuranceCardBack) {
          try {
            await executiveProtectionService.uploadMedicalAttachment(
              newPerson.id,
              formData.insuranceCardBack,
              'INSURANCE_CARD_BACK'
            );
          } catch (_uploadError) {
            // Ignore upload error
          }
        }

        // Upload consent form
        if (formData.consentForm) {
          try {
            await executiveProtectionService.uploadMedicalAttachment(
              newPerson.id,
              formData.consentForm,
              'CONSENT_FORM'
            );
          } catch (_uploadError) {
            // Ignore upload error
          }
        }

        // Refresh data to include all uploaded attachments
        queryClient.invalidateQueries({ queryKey: ['executive-protection', 'persons'] });
        queryClient.invalidateQueries({ queryKey: ['executive-protection', 'person', newPerson.id] });
      }

      // Navigate back to executive protection page
      navigate('/security/executive-protection');
    } catch (error: unknown) {
      // Set user-friendly error message
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const err = error as { response?: { data?: { error?: { details?: any[]; message?: string } } }; message?: string };

      if (err.response?.data?.error) {
        const apiErr = err.response.data.error;
        if (apiErr.details && Array.isArray(apiErr.details)) {
          // Zod validation errors
          const errorMessages = apiErr.details.map((detail: { path?: string[]; message?: string }) =>
            `${detail.path?.join('.')}: ${detail.message}`
          ).join(', ');
          setApiError(`Validation error: ${errorMessages}`);
        } else {
          setApiError(apiErr.message ?? 'Failed to create protected person');
        }
      } else if (err.message) {
        setApiError(err.message);
      } else {
        setApiError('An unexpected error occurred. Please try again.');
        console.error('Failed to add executive:', error);
      }

      // Scroll to top to show error
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/security/executive-protection');
  };

  const handleChange = (field: keyof ExecutiveFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: 'easeOut' },
    },
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="p-4 sm:p-6 space-y-6"
    >
      {/* Header */}
      <motion.div variants={itemVariants}>
        <div className="flex items-center space-x-4 mb-6">
          <Link
            to="/security/executive-protection"
            className="p-2 text-secondary-500 hover:text-secondary-700 hover:bg-secondary-100 dark:hover:bg-secondary-800 rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">
              Add Protected Person
            </h1>
            <p className="text-secondary-600 dark:text-secondary-400 mt-1">
              Create a new protected person profile
            </p>
          </div>
        </div>
      </motion.div>

      {/* Error Banner */}
      {apiError && (
        <motion.div
          variants={itemVariants}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-danger-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-danger-800 dark:text-danger-200">
                  Error Creating Protected Person
                </h3>
                <p className="mt-1 text-sm text-danger-700 dark:text-danger-300">
                  {apiError}
                </p>
              </div>
              <div className="ml-auto pl-3">
                <button
                  type="button"
                  onClick={() => setApiError(null)}
                  className="inline-flex rounded-md bg-danger-50 dark:bg-danger-900/20 p-1.5 text-danger-500 hover:bg-danger-100 dark:hover:bg-danger-900/40 focus:outline-none focus:ring-2 focus:ring-danger-600 focus:ring-offset-2"
                >
                  <span className="sr-only">Dismiss</span>
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Form */}
      <motion.div variants={itemVariants}>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information Card */}
          <Card>
            <CardHeader>
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-primary-100 dark:bg-primary-900/20 rounded-lg">
                  <UserIcon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                </div>
                <CardTitle>Basic Information</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Profile Picture */}
              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-3">
                  Profile Picture
                </label>
                <ImageUpload
                  onImageSelect={(file) => setFormData({ ...formData, profilePicture: file })}
                  maxSizeMB={5}
                />
              </div>

              {/* Name Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                    First Name <span className="text-danger-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => handleChange('firstName', e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${errors.firstName
                      ? 'border-danger-300 dark:border-danger-600'
                      : 'border-secondary-300 dark:border-secondary-600'
                      } bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white`}
                    placeholder="e.g., John"
                  />
                  {errors.firstName && (
                    <p className="mt-1 text-sm text-danger-600 dark:text-danger-400">{errors.firstName}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                    Last Name <span className="text-danger-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => handleChange('lastName', e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${errors.lastName
                      ? 'border-danger-300 dark:border-danger-600'
                      : 'border-secondary-300 dark:border-secondary-600'
                      } bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white`}
                    placeholder="e.g., Smith"
                  />
                  {errors.lastName && (
                    <p className="mt-1 text-sm text-danger-600 dark:text-danger-400">{errors.lastName}</p>
                  )}
                </div>
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                  Title/Position <span className="text-danger-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${errors.title
                    ? 'border-danger-300 dark:border-danger-600'
                    : 'border-secondary-300 dark:border-secondary-600'
                    } bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white`}
                  placeholder="e.g., Chief Executive Officer"
                />
                {errors.title && (
                  <p className="mt-1 text-sm text-danger-600 dark:text-danger-400">{errors.title}</p>
                )}
              </div>

              {/* Department */}
              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                  Department <span className="text-danger-500">*</span>
                </label>
                <div className="relative">
                  <BuildingOfficeIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-secondary-400" />
                  <input
                    type="text"
                    value={formData.department}
                    onChange={(e) => handleChange('department', e.target.value)}
                    className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${errors.department
                      ? 'border-danger-300 dark:border-danger-600'
                      : 'border-secondary-300 dark:border-secondary-600'
                      } bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white`}
                    placeholder="e.g., Executive, Finance, Technology"
                  />
                </div>
                {errors.department && (
                  <p className="mt-1 text-sm text-danger-600 dark:text-danger-400">{errors.department}</p>
                )}
              </div>

              {/* Contact Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                    Phone Number <span className="text-danger-500">*</span>
                  </label>
                  <div className="relative">
                    <PhoneIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-secondary-400" />
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${errors.phone
                        ? 'border-danger-300 dark:border-danger-600'
                        : 'border-secondary-300 dark:border-secondary-600'
                        } bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white`}
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                  {errors.phone && (
                    <p className="mt-1 text-sm text-danger-600 dark:text-danger-400">{errors.phone}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                    Email <span className="text-danger-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${errors.email
                      ? 'border-danger-300 dark:border-danger-600'
                      : 'border-secondary-300 dark:border-secondary-600'
                      } bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white`}
                    placeholder="john.smith@company.com"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-danger-600 dark:text-danger-400">{errors.email}</p>
                  )}
                </div>
              </div>

              {/* Risk Level */}
              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                  Risk Level
                </label>
                <select
                  value={formData.riskLevel}
                  onChange={(e) => handleChange('riskLevel', e.target.value)}
                  className="w-full px-4 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
            </CardContent>
          </Card>

          {/* Home Address Section */}
          <Card>
            <CardHeader>
              <CardTitle>Home Address</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                  Address Line 1
                </label>
                <input
                  type="text"
                  value={formData.homeAddressLine1}
                  onChange={(e) => handleChange('homeAddressLine1', e.target.value)}
                  className="w-full px-4 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
                  placeholder="Street address"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                  Address Line 2
                </label>
                <input
                  type="text"
                  value={formData.homeAddressLine2}
                  onChange={(e) => handleChange('homeAddressLine2', e.target.value)}
                  className="w-full px-4 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
                  placeholder="Apt, suite, unit, building, floor, etc."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                    City
                  </label>
                  <input
                    type="text"
                    value={formData.homeCity}
                    onChange={(e) => handleChange('homeCity', e.target.value)}
                    className="w-full px-4 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
                    placeholder="City"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                    State/Province
                  </label>
                  <input
                    type="text"
                    value={formData.homeState}
                    onChange={(e) => handleChange('homeState', e.target.value)}
                    className="w-full px-4 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
                    placeholder="State/Province"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                    ZIP/Postal Code
                  </label>
                  <input
                    type="text"
                    value={formData.homeZipCode}
                    onChange={(e) => handleChange('homeZipCode', e.target.value)}
                    className="w-full px-4 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
                    placeholder="ZIP/Postal Code"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                    Country
                  </label>
                  <input
                    type="text"
                    value={formData.homeCountry}
                    onChange={(e) => handleChange('homeCountry', e.target.value)}
                    className="w-full px-4 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
                    placeholder="Country"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Emergency Contact Section */}
          <Card>
            <CardHeader>
              <CardTitle>Emergency Contact</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                  Contact Name
                </label>
                <input
                  type="text"
                  value={formData.emergencyContactName}
                  onChange={(e) => handleChange('emergencyContactName', e.target.value)}
                  className="w-full px-4 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
                  placeholder="e.g., Jane Smith"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                  Relationship
                </label>
                <input
                  type="text"
                  value={formData.emergencyContactRelationship}
                  onChange={(e) => handleChange('emergencyContactRelationship', e.target.value)}
                  className="w-full px-4 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
                  placeholder="e.g., Spouse, Sibling, Parent"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={formData.emergencyContactPhone}
                    onChange={(e) => handleChange('emergencyContactPhone', e.target.value)}
                    className="w-full px-4 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.emergencyContactEmail}
                    onChange={(e) => handleChange('emergencyContactEmail', e.target.value)}
                    className="w-full px-4 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
                    placeholder="emergency@example.com"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Medical Information Section */}
          <Card>
            <CardHeader>
              <CardTitle>Medical Information</CardTitle>
            </CardHeader>
            <CardContent>
              <MedicalInformationSection
                isEditing={true}
                medicalData={{
                  insuranceProvider: formData.insuranceProvider,
                  insurancePolicyNumber: formData.insurancePolicyNumber,
                  insuranceGroupNumber: formData.insuranceGroupNumber,
                  primaryDoctorName: formData.primaryDoctorName,
                  primaryDoctorPhone: formData.primaryDoctorPhone,
                  primaryDoctorAddress: formData.primaryDoctorAddress,
                  medicalConditions: formData.medicalConditions ?? [],
                  medicalDisclosureConsent: formData.medicalDisclosureConsent,
                  consentDate: formData.consentDate,
                  consentExpiresAt: formData.consentExpiresAt,
                }}
                onMedicalDataChange={(data) => {
                  setFormData(prev => ({ ...prev, ...data }));
                }}
                onInsuranceCardUpload={(file, type) => {
                  if (type === 'front') {
                    setFormData(prev => ({ ...prev, insuranceCardFront: file }));
                  } else {
                    setFormData(prev => ({ ...prev, insuranceCardBack: file }));
                  }
                  return Promise.resolve();
                }}
                onConsentFormUpload={(file) => {
                  setFormData(prev => ({ ...prev, consentForm: file }));
                  return Promise.resolve();
                }}
                errors={errors}
              />
            </CardContent>
          </Card>

          {/* Notes */}
          <Card>
            <CardHeader>
              <CardTitle>Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <textarea
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                rows={3}
                className="w-full px-4 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
                placeholder="Additional information about this executive..."
              />
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Protected Person'}
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}
